import Geolocation from '@react-native-community/geolocation';
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {Platform, AppState, AppStateStatus, PermissionsAndroid} from 'react-native';
import {api} from '../services/api';
import {setCidadeRastrear, setRastrearGeoOff, setUfRastrear} from '../services/rastrear';
import {getDistance} from 'geolib';

interface LocationInfo {
  cidade: string;
  uf: string;
}

interface LocalizationContextData {
  setPosition: React.Dispatch<
    React.SetStateAction<{
      latitude: number;
      longitude: number;
    } | null>
  >;
  position: {latitude: number; longitude: number} | null;
  geolocation?: LocationInfo;
  isLocationLoading: boolean;
  locationIsActive: boolean;
  getCurrentCity: () => Promise<void>;
  refreshLocation: () => void;
}

interface LocalizationProviderProps {
  children: ReactNode;
}

const LocalizationContext = createContext<LocalizationContextData>(
  {} as LocalizationContextData,
);

function LocalizationProvider({children}: LocalizationProviderProps) {
  const [position, setPosition] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const subscriptionIdRef = useRef<number | null>(null);
  const [geolocation, setGeolocation] = useState<LocationInfo>();
  const lastPositionRef = useRef<{latitude: number; longitude: number} | null>(
    null,
  );
  const lastWatchPositionRef = useRef<
    {latitude: number; longitude: number} | null
  >(null);
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);

  const locationIsActive = position !== null && position.latitude !== 0 && position.longitude !== 0;

  const [isLocationLoading, setIsLocationLoading] = useState(true);

  const ensureAndroidLocationPermission = useCallback(
    async (requestIfNeeded: boolean) => {
      const hasPermission = await PermissionsAndroid.check(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
      );

      if (hasPermission) {
        return true;
      }

      if (!requestIfNeeded) {
        return false;
      }

      const status = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
      );

      return status === PermissionsAndroid.RESULTS.GRANTED;
    },
    [],
  );

  const startWatching = useCallback(async (requestIfNeeded = false) => {
    // Limpa watch anterior se existir
    if (subscriptionIdRef.current !== null) {
      Geolocation.clearWatch(subscriptionIdRef.current);
      subscriptionIdRef.current = null;
    }

    // No Android, verificar permissão ANTES de chamar watchPosition.
    // Sem isso, o geolocation chama o error callback em loop (~15x/s) quando
    // a permissão é negada, pois o timer nativo não para por si só.

    if (Platform.OS === 'android') {
      const granted = await ensureAndroidLocationPermission(requestIfNeeded);
      if (!granted) {
        setIsLocationLoading(false);
        return;
      }
    }

    const watchID = Geolocation.watchPosition(
      p => {
        const next = {
          latitude: p.coords.latitude,
          longitude: p.coords.longitude,
        };
        const last = lastWatchPositionRef.current;

        // Só atualiza a referência de posição se o dispositivo realmente se moveu.
        // watchPosition dispara com frequência mesmo parado (jitter do GPS),
        // e um novo objeto a cada callback recriava `position`, disparando
        // re-renders/refetches em quem depende dele (ex: NearbyStationSuggestion),
        // o que podia desmontar um StationItem no meio de uma interação do usuário.
        if (!last || getDistance(last, next) > 50) {
          lastWatchPositionRef.current = next;
          setPosition(next);
        }

        setIsLocationLoading(false);
      },
      err => {
        console.log('watch error', err);
        // Limpa o watch ao receber erro para evitar callbacks repetidos
        if (subscriptionIdRef.current !== null) {
          Geolocation.clearWatch(subscriptionIdRef.current);
          subscriptionIdRef.current = null;
        }
        setIsLocationLoading(false);
      },
      {enableHighAccuracy: true, timeout: 15000, maximumAge: 10000},
    );
    subscriptionIdRef.current = watchID as unknown as number;
  }, [ensureAndroidLocationPermission]);

  const stopWatching = useCallback(() => {
    if (subscriptionIdRef.current !== null) {
      Geolocation.clearWatch(subscriptionIdRef.current);
      subscriptionIdRef.current = null;
    }
    setPosition(null);
    setIsLocationLoading(false);
  }, []);

  const refreshLocation = useCallback(() => {
    if (Platform.OS === 'web') {return;}
    // Não exibe loading em refreshes silenciosos (app voltando ao foreground)
    // para evitar re-renders desnecessários em todos os consumers do contexto
    startWatching(false);
  }, [startWatching]);

  // Listener para quando o app volta do background
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
      if (
        appStateRef.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        refreshLocation();
      }
      appStateRef.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [refreshLocation]);

  // Setup inicial
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    if (Platform.OS === 'android') {
      startWatching(true);
    } else {
      // No iOS, requestAuthorization deve ser chamado antes de iniciar o watch.
      // Versões antigas aceitam (successCb, errorCb); versões novas retornam Promise.
      // Para cobrir ambos os casos, chamamos requestAuthorization e garantimos que
      // startWatching sempre seja invocado independentemente da assinatura da API.
      const auth = (Geolocation as any).requestAuthorization;
      if (typeof auth === 'function') {
        const result = auth.call(Geolocation, startWatching, stopWatching);
        // API baseada em Promise (versões mais novas da lib)
        if (result && typeof result.then === 'function') {
          result
            .then(() => startWatching(false))
            .catch(() => setIsLocationLoading(false));
        }
        // Se for callback-based, startWatching já foi passado como arg acima
      } else {
        startWatching(false);
      }
    }

    return () => {
      stopWatching();
    };

  }, []);

  // Só marca a geo como desligada depois que a resolução terminou (permissão
  // negada, GPS indisponível etc.), para não enviar "geoOff" enquanto o
  // primeiro fix de posição ainda está a caminho.
  useEffect(() => {
    if (Platform.OS === 'web' || isLocationLoading) {return;}
    setRastrearGeoOff(!locationIsActive);
  }, [isLocationLoading, locationIsActive]);

  const getCurrentCity = useCallback(async () => {
    if (!position) {return;}

    const current = {
      latitude: position.latitude,
      longitude: position.longitude,
    };
    const last = lastPositionRef.current;

    // Só continua se for a primeira vez OU se a distância for maior que 100m
    if (last) {
      const distance = getDistance(last, current);
      if (distance <= 100) {return;}
    }

    try {
      const {data} = await api.get('/geolocalizacao', {
        params: {lat: current.latitude, lng: current.longitude},
      });

      console.log('Localização atual:', data);

      setGeolocation(data);
      lastPositionRef.current = current;
      if (data?.cidade) {
        setCidadeRastrear(data.cidade);
      }
      if (data?.uf) {
        setUfRastrear(data.uf);
      }
    } catch (error: any) {
      console.error('Erro ao tentar pegar localizacao na API', error);
    }
  }, [position]);

  useEffect(() => {
    getCurrentCity();
  }, [getCurrentCity]);

  const contextValue = useMemo(
    () => ({
      setPosition,
      position,
      locationIsActive,
      getCurrentCity,
      geolocation,
      isLocationLoading,
      refreshLocation,
    }),
    [position, locationIsActive, getCurrentCity, geolocation, isLocationLoading, refreshLocation],
  );

  return (
    <LocalizationContext.Provider value={contextValue}>
      {children}
    </LocalizationContext.Provider>
  );
}

function useLocalization() {
  const context = useContext(LocalizationContext);
  return context;
}

export {useLocalization, LocalizationProvider};
